import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import { SERVER_URL } from "../config/config";
import "./popup.css";
import "./EditHistoryPopup.css";

const EMPTY_LABEL = "Empty";

const prettifyFieldName = (fieldName) => {
  if (!fieldName) {
    return "Field";
  }

  return String(fieldName)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isDateLikeValue = (value) => {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value);
};

const formatDateValue = (value) => {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatHistoryValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_LABEL;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (isDateLikeValue(value)) {
    return formatDateValue(value);
  }

  return String(value);
};

const getErrorMessage = async (response) => {
  try {
    const errorData = await response.json();

    if (typeof errorData.detail === "string") {
      return errorData.detail;
    }

    if (Array.isArray(errorData.detail)) {
      return errorData.detail
        .map((item) => item.msg || item.message || JSON.stringify(item))
        .join(", ");
    }

    if (typeof errorData.message === "string") {
      return errorData.message;
    }

    return "Unable to fetch edit history.";
  } catch (error) {
    return "Unable to fetch edit history.";
  }
};

const normalizeRecordIds = (recordIds) => {
  return [
    ...new Set(
      (recordIds || []).filter(
        (value) => value !== null && value !== undefined && value !== "",
      ),
    ),
  ];
};

const fetchHistoryData = async (moduleName, recordIds) => {
  const idsToTry = normalizeRecordIds(recordIds);

  if (idsToTry.length === 0) {
    throw new Error(
      "History is available only after the record has been loaded.",
    );
  }

  let fallbackResponse = null;
  let lastErrorMessage = "Unable to fetch edit history.";

  for (const recordId of idsToTry) {
    const response = await fetch(
      `${SERVER_URL}/history/table/?record_id=${encodeURIComponent(recordId)}&module_name=${encodeURIComponent(moduleName)}`,
      {
        method: "POST",
      },
    );

    if (!response.ok) {
      lastErrorMessage = await getErrorMessage(response);
      continue;
    }

    const data = await response.json();

    if (!fallbackResponse) {
      fallbackResponse = data;
    }

    if ((data?.total_rows || 0) > 0) {
      return data;
    }
  }

  if (fallbackResponse) {
    return fallbackResponse;
  }

  throw new Error(lastErrorMessage);
};

const EditHistoryPopup = ({
  moduleName,
  recordIds,
  label = "View Edit History",
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const normalizedRecordIds = useMemo(
    () => normalizeRecordIds(recordIds),
    [recordIds],
  );
  const canOpenHistory = normalizedRecordIds.length > 0;

  useEffect(() => {
    let isMounted = true;

    if (!open) {
      return undefined;
    }

    const loadHistory = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetchHistoryData(
          moduleName,
          normalizedRecordIds,
        );

        if (!isMounted) {
          return;
        }

        setHistoryData(Array.isArray(response?.data) ? response.data : []);
        setTotalRows(response?.total_rows || 0);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setHistoryData([]);
        setTotalRows(0);
        setError(err.message || "Unable to fetch edit history.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [moduleName, normalizedRecordIds, open]);

  const groupedHistory = useMemo(() => {
    const groups = {};

    historyData.forEach((item) => {
      const groupKey = `${item.changed_at || ""}_${item.changed_by || ""}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          changedAt: item.changed_at,
          changedByName: item.changed_by_name || "Unknown User",
          rows: [],
        };
      }

      groups[groupKey].rows.push(item);
    });

    return Object.values(groups);
  }, [historyData]);

  if (!canOpenHistory) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-primary edit-history-button"
        onClick={() => setOpen(true)}
      >
        {label}
      </button>

      <Popup modal open={open} onClose={() => setOpen(false)}>
        <div className="edit-history-popup">
          <div className="edit-history-popup__header">
            <div>
              <div className="edit-history-popup__title">Edit History</div>
              <div className="edit-history-popup__subtitle">
                Module: {prettifyFieldName(moduleName)}
              </div>
            </div>
            <button
              type="button"
              className="edit-history-popup__close"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>

          <div className="edit-history-popup__meta">
            <div className="edit-history-popup__meta-pill">
              Changes: {totalRows}
            </div>
            {/* <div className="edit-history-popup__meta-pill">
              Record Id: {normalizedRecordIds[0]}
            </div> */}
          </div>

          {loading && (
            <div className="edit-history-popup__state">
              Loading edit history...
            </div>
          )}

          {!loading && error && (
            <div className="edit-history-popup__state edit-history-popup__state--error">
              {error}
            </div>
          )}

          {!loading && !error && groupedHistory.length === 0 && (
            <div className="edit-history-popup__state">
              No edit history found for this record.
            </div>
          )}

          {!loading && !error && groupedHistory.length > 0 && (
            <div className="edit-history-popup__timeline">
              {groupedHistory.map((group, groupIndex) => (
                <div
                  className="edit-history-popup__card"
                  key={`${group.changedAt}_${groupIndex}`}
                >
                  <div className="edit-history-popup__card-header">
                    <div>
                      <div className="edit-history-popup__card-label">
                        Edited By
                      </div>

                      <div className="edit-history-popup__card-user">
                        {group.changedByName}
                      </div>
                    </div>

                    <div>
                      <div className="edit-history-popup__card-label">
                        Changed At
                      </div>

                      <div className="edit-history-popup__card-time">
                        {formatDateValue(group.changedAt)}
                      </div>
                    </div>
                  </div>

                  <div className="edit-history-popup__table-wrap">
                    <table className="edit-history-popup__table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => (
                          <tr key={row.id}>
                            <td>{prettifyFieldName(row.field_name)}</td>
                            <td>{formatHistoryValue(row.old_value)}</td>
                            <td>{formatHistoryValue(row.new_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default EditHistoryPopup;
